import { FC } from 'react'
import { observer } from 'mobx-react'
import { useTranslation } from 'next-i18next'
import * as Yup from 'yup'
import { Field, Form, Formik } from 'formik'
import { useRouter } from 'next/router'
import { getUserAvatar, useStores } from '../../../utils'
import styles from './index.module.scss'
import { IJobBase, MessageType } from '../../../types'
import { Button } from '../../atoms/Button'
import { Avatar } from '../../atoms/Avatar'
import { VerifiedName } from '../../atoms/VerifiedName'
import { TextAreaGroup } from '../TextAreaGroup'
import { ValidationErrorMessage } from '../../atoms/ValidationErrorMessage'

type Props = {
  job: IJobBase
}

type Value = {
  message: string
}

export const JobApplicationModalRightContent: FC<Props> = observer(({ job }) => {
  const { t } = useTranslation(['companies-users-[id]', 'validation'])
  const router = useRouter()
  const { ui, messages, viewer } = useStores()

  const initialValues: Value = {
    message: '',
  }

  const validationSchema = Yup.object().shape({
    message: Yup.string().required(t('validation:必須項目です')),
  })

  const isAppliable = viewer.viewer.profile?.avatar?.length > 0 && viewer.viewer.profile?.bio?.length > 0

  const fontSize = ui.isMobile ? 'medium' : 'large'

  return (
    <>
      <div className={styles.jobNameItem}>
        <p className={styles.jobNameLabel}>{t('応募する募集')}</p>
        <div className={styles.jobName}>{job?.title}</div>
      </div>

      <div className={styles.item}>
        <p className={styles.itemLabel}>{t('採用担当者')}</p>
        <div className={styles.recruitor}>
          <div className={styles.avatar}>
            <Avatar src={getUserAvatar(job?.primaryRecruitingContact)} alt={job?.primaryRecruitingContact?.name} />
          </div>
          <div className={styles.nameContent}>
            <div className={styles.name}>{job?.company?.name}</div>
            <VerifiedName
              name={job?.primaryRecruitingContact?.name}
              isVerifiedAccount={job?.primaryRecruitingContact?.profile?.isVerifiedAccount}
            />
          </div>
        </div>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (value: Value, actions) => {
          const isSuccessful = await viewer?.applyForJob(job?.id, value.message)
          actions.setSubmitting(false)

          if (isSuccessful) {
            messages.add({
              type: MessageType.Info,
              body: 'Applied for project',
              ttl: 5000,
            })
            // 成功したときだけフォームリセットして閉じる
            actions.resetForm()
            ui.toggleJobApplicationModal()
            router.push('/messages')
          }
        }}
      >
        {({ isSubmitting, isValid }) => (
          <Form>
            <div className={styles.messageItem}>
              <p className={styles.itemLabel}>{t('メッセージ')}</p>
              <Field
                name='message'
                type='text'
                placeholder={t(
                  'あなたの応募動機や意気込みを担当者に伝えましょう！\nまずは話を聞いてみたいという場合は、その旨を採用担当者に伝えましょう。\nプロフィールを充実させることでより返信率が高くなります。'
                )}
                fieldType='setting'
                disabled={!isAppliable}
                component={TextAreaGroup}
              />
            </div>
            <div className={styles.submitGroup}>
              <div className={styles.submitButton}>
                <Button
                  styleType='blue'
                  componentType='button'
                  buttonType='submit'
                  buttonSize='large'
                  disabled={isSubmitting || !isValid || !isAppliable}
                  isLoading={isSubmitting}
                  fontSize={fontSize}
                >
                  {t('応募する')}
                </Button>
              </div>
              {!isAppliable && (
                <div className={styles.errorMessage}>
                  <ValidationErrorMessage>
                    {t('応募するには顔写真と自己紹介を設定する必要があります')}
                  </ValidationErrorMessage>
                </div>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </>
  )
})
